import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Space, Tag, Button } from 'antd';
import { normalizeBase } from '@digiwedge/ui-health-panels';

export interface CdnDiagnosticsPanelProps {
  cdnBase: string;
}

export default function CdnDiagnosticsPanel({ cdnBase }: CdnDiagnosticsPanelProps) {
  const { Text } = Typography;
  const sriUrl = useMemo(() => (cdnBase ? `${normalizeBase(cdnBase)}.sri` : ''), [cdnBase]);
  const [status, setStatus] = useState<'idle' | 'ok' | 'fail'>('idle');
  const [sriText, setSriText] = useState<string>('');

  useEffect(() => {
    let alive = true;
    async function run() {
      if (!sriUrl) return;
      try {
        const res = await fetch(sriUrl, { credentials: 'omit' });
        const txt = await res.text();
        if (!alive) return;
        setStatus(res.ok ? 'ok' : 'fail');
        setSriText(txt);
      } catch {
        if (!alive) return;
        setStatus('fail');
        setSriText('');
      }
    }
    void run();
    return () => {
      alive = false;
    };
  }, [sriUrl]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sriText);
    } catch {
      /* ignore */
    }
  };

  const color = status === 'ok' ? 'green' : status === 'fail' ? 'red' : 'default';

  return (
    <Card title="CDN Diagnostics" extra={<Tag color={color}>{status.toUpperCase()}</Tag>}>
      {sriUrl ? (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <Text strong>.sri</Text>
            <a href={sriUrl} target="_blank" rel="noreferrer">
              {sriUrl}
            </a>
            <Button size="small" onClick={copy} disabled={!sriText}>
              Copy
            </Button>
          </Space>
          <pre style={{ margin: 0, maxHeight: 200, overflow: 'auto' }}>{sriText || '—'}</pre>
        </Space>
      ) : (
        <Tag>Not configured</Tag>
      )}
    </Card>
  );
}
